import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Mic, Search, Star, Hospital, ArrowLeftIcon, Info, ArrowLeft, Filter, Video, MessageSquare, CheckCircle, ChevronRight,  ShoppingCart, Calendar1Icon, User, CalendarX, MapPin, ArrowRight, Clock, Stethoscope, Brain, Heart, View, UserPlus, LucideTable, LocateIcon, ShieldCheck } from 'lucide-react';
import BottomNavigation from '../components/bottom_navigation';
import FeatureHeader from '../components/FeatureHeader';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { fetchUpcomingConsultationsWithDoctors } from '../utils/appointments';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentCoordinates } from '../lib/nativeGeolocation';

import pain from "../assets/icons/pain.jpg";
import purple from "../assets/icons/purple-pain.jpeg";
import intestines from "../assets/icons/intestine.png";
import surg from "../assets/icons/surg.png";
import heart from "../assets/icons/heart.png";

import digestion from "../assets/icons/digestion.png";
import surgeon from "../assets/icons/surgeon.png";

import physician from "../assets/icons/physician.png";
import brain from "../assets/icons/brain.png";
import pediatrics from "../assets/icons/pediatrics.png";
import skincare from "../assets/icons/skincare.png";
import pregnant from "../assets/icons/pregnant.png";
import mindIcon from "../assets/icons/mind.png";
import sexualHealth from "../assets/icons/sexual-health.png";
import stethoscope from "../assets/icons/stethoscope.png";
import dc1 from "../assets/banner/DC 1.jpg";
import dc2 from "../assets/banner/DC 2.jpg";
import dc3 from "../assets/banner/DC 3.jpg";


import DoctorCard from '../components/DoctorCard';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { text } from '@cloudinary/url-gen/qualifiers/source';

const doctorTrustHighlights = [
  { Icon: ShieldCheck, title: 'Licensed & Verified', subtitle: 'All doctors are certified' },
  { Icon: User,        title: 'Doctor Reviewed',     subtitle: 'Every consultation' },
  { Icon: ShieldCheck, title: '100% Confidential',   subtitle: 'Your privacy is our priority' },
  { Icon: CheckCircle, title: 'Board Certified',      subtitle: 'Specialist expertise' },
  { Icon: Star,        title: 'Highly Rated',         subtitle: 'Top-rated by patients' },
];

export default function DoctorDirectory() {
  const { profile } = useAuth();
  const user = profile;
    const [activeTopCategory, setActiveTopCategory] = useState("All");
  const navigate = useNavigate();
    const handleCategoryChange = (name) => {
    setActiveTopCategory(name);
  };
  const categories = [
    "All",
    "General Practitioner",
    "Cardiologist",
    "Neurologist",
    "Gastroenterologist",
    "Pediatrician",
    "Dermatologist",
    "Gynecologist",
    "General Surgeon",
    "Mental Health",
    "Sexual Health",
  ];
  const [search, setSearch] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [nearbyDoctors, setNearbyDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const trustStripRef = useRef(null);
  const [isTrustStripPaused, setIsTrustStripPaused] = useState(false);

  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [loadingNearby, setLoadingNearby] = useState(true);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
  const [recommend, setRecommended] = useState([])
  const [location, setLocation] = useState({ lat: null, lon: null });
  const [locationName, setLocationName] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('All Specialists');
  const [isListening, setIsListening] = useState(false);
  const [query, setQuery] = useState('');
  const recognitionRef = useRef(null);

  const normalizeText = useCallback((value) => String(value || '').trim().toLowerCase(), []);

  const getDoctorSpecialtyText = useCallback((doctor) => {
    const specialtyValue = doctor?.specialty || doctor?.specialisation || doctor?.specialization || doctor?.category || '';
    if (Array.isArray(specialtyValue)) {
      return specialtyValue.map((item) => normalizeText(item)).join(' ');
    }
    return normalizeText(specialtyValue);
  }, [normalizeText]);

  const categoryAliases = useMemo(() => ({
    'general practitioner': ['general practitioner', 'general medicine', 'family medicine', 'family physician', 'physician', 'general'],
    cardiologist: ['cardiologist', 'cardiology', 'heart'],
    neurologist: ['neurologist', 'neurology', 'brain'],
    gastroenterologist: ['gastroenterologist', 'gastroenterology', 'digestive', 'gi'],
    pediatrician: ['pediatrician', 'pediatrics', 'paediatric', 'child'],
    dermatologist: ['dermatologist', 'dermatology', 'skin'],
    gynecologist: ['gynecologist', 'gynaecologist', 'obgyn', 'ob-gyn', 'obstetrician', 'gynecology', 'women'],
    'general surgeon': ['general surgeon', 'surgeon', 'surgery'],
    'mental health': ['mental health', 'psychiatry', 'psychiatrist', 'psychology', 'psychologist', 'counselor', 'therapy'],
    'sexual health': ['sexual health', 'andrology', 'urology', 'reproductive'],
  }), []);

  const matchesCategory = useCallback((doctor, category) => {
    const normalizedCategory = normalizeText(category);
    if (normalizedCategory === 'all') return true;

    const specialtyText = getDoctorSpecialtyText(doctor);
    if (!specialtyText) return false;

    const aliases = categoryAliases[normalizedCategory] || [normalizedCategory];
    return aliases.some((alias) => specialtyText.includes(alias));
  }, [categoryAliases, getDoctorSpecialtyText, normalizeText]);

  const filteredDoctorsByTopCategory = useMemo(
    () => doctors.filter((doctor) => matchesCategory(doctor, activeTopCategory)),
    [activeTopCategory, doctors, matchesCategory]
  );

  const filteredRecommendedByTopCategory = useMemo(
    () => recommend.filter((doctor) => matchesCategory(doctor, activeTopCategory)),
    [activeTopCategory, matchesCategory, recommend]
  );

  useEffect(() => {
    const strip = trustStripRef.current;
    if (!strip || isTrustStripPaused) return;
    const interval = setInterval(() => {
      const resetPoint = strip.scrollWidth / 2;
      if (resetPoint <= 0) return;
      if (strip.scrollLeft >= resetPoint) {
        strip.scrollLeft -= resetPoint;
      }
      strip.scrollBy({ left: 1, behavior: 'auto' });
    }, 24);
    return () => clearInterval(interval);
  }, [isTrustStripPaused]);

    const handleMouseEnter = useCallback((e) => {
      e.currentTarget.style.background = "rgba(255,255,255,0.35)";
    }, []);
  
    const handleMouseLeave = useCallback((e) => {
      e.currentTarget.style.background = "rgba(255,255,255,0.2)";
    }, []);


//Fetch Location Data 
useEffect(()=>{
const getLocation = async () => {
    const result = await getCurrentCoordinates();
    if (!result.ok) {
      console.error('Failed to get location:', result.error);
      return;
    }

    setLocation({
      lat: result.coords.lat,
      lon: result.coords.lon,
    });
    console.log(result.coords.lat);
  };

getLocation();
}, [])



const doctors_ = [
  {
    name: 'Dr. Samuel Teambo',
    specialty: 'Cardiologist',
    status: 'Online now',
    distance: null,
    verified: true,
    image: 'https://media.post.rvohealth.io/wp-content/uploads/2020/09/Black_Male_Doctor_Portrait_732x549-thumbnail.jpg',
  },
  {
    name: 'Dr. Linda Kamara',
    specialty: 'General Practitioner',
    status: 'Available Today',
    distance: null,
    verified: true,
    image: 'https://tse2.mm.bing.net/th/id/OIP.C8KxF1G9m9JGs1EX_5fq0QHaJQ?rs=1&pid=ImgDetMain&o=7&rm=3',
  },
  {
    name: 'Dr. Joseph Mensah',
    specialty: 'Pediatrician',
    status: null,
    distance: '1.2 km away',
    verified: true,
    image: 'https://thumbs.dreamstime.com/b/doctor-looking-directly-to-camera-11126265.jpg',
  },
];


// Reverse-geocode lat/lon to a friendly place name (uses Nominatim)
useEffect(() => {
  const fetchLocationName = async () => {
    const { lat, lon } = location;
    if (!lat || !lon) return;
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`);
      if (!res.ok) throw new Error('Reverse geocode failed');
      const data = await res.json();
      const addr = data.address || {};
      const name = addr.city || addr.town || addr.village || addr.suburb || addr.neighbourhood || addr.county || data.display_name;
      setLocationName(name || '');
    } catch (err) {
      console.error('Failed to reverse geocode location:', err);
    }
  };
  fetchLocationName();
}, [location]);
  // Fetch all doctors
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoadingDoctors(true);
        const response = await api.get('/api/doctor/smart?online=true?recommended=true');
        console.log("smart doctors", response.data)
        setDoctors(response.data.doctors);

        const rec = await api.get('/api/doctor/smart?recommended=true');
        console.log("rec", rec.data)
        setRecommended(rec.data.doctors)
      } catch (err) {
        console.error('Failed to fetch doctors:', err);
      } finally {
        setLoadingDoctors(false);
      }
    };
    fetchDoctors();
  }, []);

  // Fetch nearby doctors
  useEffect(() => {
    const fetchNearby = async () => {
      const { lat, lon } = location;

      // wait until geolocation is populated
      if (lat == null || lon == null) return;

      try {
        setLoadingNearby(true);
        const res = await api.get(`/api/doctor/nearby?lat=${lat}&lng=${lon}&radius=5`);
        setNearbyDoctors(res.data);
      } catch (err) {
        console.error("Failed to fetch nearby doctors:", err);
      } finally {
        setLoadingNearby(false);
      }
    };

    fetchNearby();
  }, [user, location]);

  // Fetch consultations + doctor's info
  useEffect(() => {
    const fetchData = async () => {
      if (!user?._id) return;
      setLoadingAppointments(true);
      try {
        const appts = await fetchUpcomingConsultationsWithDoctors(user._id);
        setAppointments(appts);
      } catch (err) {
        setAppointments([]);
      } finally {
        setLoadingAppointments(false);
      }
    };
    fetchData();
  }, [user]);

  const carouselStyle = {
    display: "flex",
    gap: 16,
    overflowX: "auto",
    scrollSnapType: "x mandatory", // enables snapping to each card
    
    WebkitOverflowScrolling: "touch", // smooth scrolling on iOS
  };

  const cardWrapperStyle = {
    flex: "0 0 auto", // prevents shrinking
    scrollSnapAlign: "start", // each card snaps to start
  };
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const submitSearch = useCallback((value = query) => {
    const nextQuery = value.trim();
    if (!nextQuery) {
      navigate('/search');
      return;
    }

    navigate(`/search?q=${encodeURIComponent(nextQuery)}`);
  }, [navigate, query]);

  const handleMicClick = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      submitSearch();
      return;
    }

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript?.trim() || '';
      if (!transcript) return;
      setQuery(transcript);
      navigate(`/search?q=${encodeURIComponent(transcript)}`);
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [isListening, navigate, submitSearch]);

  const filteredAppointments = appointments.filter(a => ['scheduled', 'ongoing'].includes((a.status || '').toLowerCase()));

  return (
    <div className="min-vh-100"  style={{ backgroundColor: 'var(--bg)', minHeight: '100vh', paddingBottom: 80 }}>

      <style>{`
        .dd-card { transition: transform .18s ease, box-shadow .18s ease; border-radius: 12px; }
        .dd-card:hover { transform: translateY(-6px); box-shadow: 0 18px 40px rgba(2,6,23,0.12); }
        .doctor-card-fixed-width {
          min-width: 220px;
          max-width: 240px;
          width: 230px;
        }
        .specialty-pill[aria-pressed='true'] { transform: translateY(-6px); }
        .pill-bounce { transition: transform .28s cubic-bezier(.2,.8,.2,1); }
        .book-pulse { animation: pulse 2s infinite; }
        @keyframes pulse { 0% { box-shadow: 0 0 0 0 rgba(99,102,241,0.25); } 70% { box-shadow: 0 0 0 8px rgba(99,102,241,0); } 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0); } }
        .carousel-small { display:flex; gap:20px; overflow-x:auto; padding-bottom:6px; scroll-snap-type:x mandatory; }
        .carousel-item { scroll-snap-align:center; min-width:110px; display:flex; flex-direction:column; align-items:center; gap:10px }
        .doctor-two-row-scroll {
          display: grid;
          grid-auto-flow: column;
          grid-template-rows: repeat(2, minmax(0, 1fr));
          grid-auto-columns: minmax(210px, 240px);
          row-gap: 0px;
          column-gap: 20px;
          overflow-x: auto;
          padding-bottom: 2px;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .doctor-two-row-scroll::-webkit-scrollbar { display: none; }
        .doctor-single-row-scroll {
          display: flex;
          gap: 2px;
          overflow-x: auto;
          padding-bottom: 2px;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .doctor-single-row-scroll::-webkit-scrollbar { display: none; }
      `}</style>
{/* Hero Section */}

      <div className='container' >
 <div style={styles.header}>
    <FeatureHeader
      title="Doctor Consultation"
      subtitle="Get medical advice anytime, anywhere"
      onBack={() => navigate("/home")}
      rightSlot={(
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div
            style={styles.cartIcon}
            onClick={() => navigate("/appoint")}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <i class="fi fi-tr-calendar-clock" style={{color : "var(--primary)", fontSize : 20}}></i>
          </div>
          <div
            style={styles.cartIcon}
            onClick={() => navigate("/consultation-chats")}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            title="Consultation chats"
          >
            <MessageSquare size={20} color="var(--primary)" />
          </div>
        </div>
      )}
      containerStyle={{ padding: 0 }}
    />

   

    
    


   


 
    {/* Trust Badges - auto-scrolling strip 
    <div
      ref={trustStripRef}
      className="trust-strip-scroll"
      style={styles.trustStrip}
      onMouseEnter={() => setIsTrustStripPaused(true)}
      onMouseLeave={() => setIsTrustStripPaused(false)}
      onTouchStart={() => setIsTrustStripPaused(true)}
      onTouchEnd={() => setIsTrustStripPaused(false)}
    >
      <div style={styles.trustTrack}>
        {[...doctorTrustHighlights, ...doctorTrustHighlights].map((item, index, list) => {
          const Icon = item.Icon;
          return (
            <React.Fragment key={`${item.title}-${index}`}>
              <div style={styles.trustItem}>
                <div style={styles.trustItemIconWrap}>
                  <Icon size={13} color="var(--primary)" />
                </div>
                <div style={styles.trustItemCopy}>
                  <span style={styles.trustItemTitle}>{item.title}</span>
                  <span style={styles.trustItemSubtitle}>{item.subtitle}</span>
                </div>
              </div>
              {index < list.length - 1 && <div style={styles.trustStripDivider}></div>}
            </React.Fragment>
          );
        })}
      </div>
    </div>
*/}
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 64,
          borderRadius: 32,
          padding: '6px 10px',
          background: '#f7f4ff',
          border: '1px solid #ece5ff',
          boxShadow: '0 2px 8px rgba(124,77,255,0.08)',
        }}
      >
        <div
          onClick={() => navigate('/asha-cover')}
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: '#efe8ff',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            cursor: 'pointer',
          }}
        >
          <AutoAwesomeIcon style={{ fontSize: 20, color: 'var(--primary)' }} />
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            submitSearch();
          }}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            position: 'relative',
            background: '#fff',
            border: '1px solid #eee8ff',
            borderRadius: 26,
            padding: '0 46px 0 12px',
            height: 50,
            margin: 0,
          }}
        >
          <Search size={17} color="var(--primary)" style={{ flexShrink: 0 }} />
          <input
            type="text"
            placeholder="What do you need help with?"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 10,
              color: '#8a8a95',
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              height: 22,
              paddingLeft: 10,
              borderLeft: '1px solid #e6e6e8',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Mic
              size={19}
              color={isListening ? '#dc2626' : 'var(--primary)'}
              style={{ cursor: 'pointer' }}
              onClick={handleMicClick}
            />
          </div>
        </form>
      </div>
    </div>
  </div>

  {/* Smartcheck Card  */}

  <AIDoctorBanner/>
    


         <br></br>

{/* Health Categories Section */}
  <div style={{ ...styles.sectionHeader, marginBottom: 8 }}>
    <h6 style={styles.sectionTitle}>Top Health Categories</h6>
    <button style={styles.seeAllButton} onClick={() => navigate('/all-doctors')}>
      View All <ChevronRight size={16} />
    </button>
  </div>
  <div className="d-flex gap-2 overflow-auto mb-3" style={{ scrollbarWidth: 'none' }}>
    {categories.map((cat) => (
      <button
        key={cat}
        onClick={() => handleCategoryChange(cat)}
        className="btn"
        style={{
          borderRadius: 20,
          fontSize: 13,
          fontWeight: 500,
          whiteSpace: "nowrap",
          padding: '6px 16px',
          background: activeTopCategory === cat ? "var(--primary)" : "var(--surface)",
          color: activeTopCategory === cat ? '#fff' : 'var(--on-bg)',
          border: activeTopCategory === cat ? 'none' : '1px solid #e5e7eb',
          transition: 'all 0.18s ease',
          flexShrink: 0,
        }}
      >
        {cat}
      </button>
    ))}
  </div>
  <div style={{ marginBottom: 4 }}>
    <div style={styles.sectionHeader}>
      <h6 style={styles.sectionTitle}>
        {activeTopCategory === 'All' ? 'Doctors By Category' : `${activeTopCategory} Doctors`}
      </h6>
      <button
        style={styles.seeAllButton}
        onClick={() => navigate('/all-doctors')}
      >
        See All <ChevronRight size={16} />
      </button>
    </div>

    <div className="doctor-two-row-scroll" >
      {filteredDoctorsByTopCategory.slice(0, 8).map((doc, idx) => (
        <div className="doctor-card-fixed-width" key={`category-${idx}`}> <DoctorCard doctor={doc} /> </div>
      ))}
    </div>

    {!loadingDoctors && filteredDoctorsByTopCategory.length === 0 ? (
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
        No doctors found in this category yet.
      </div>
    ) : null}
  </div>

        {/* Available Now */}
        <div style={{ marginTop: 8 }}>
          <div style={styles.sectionHeader}>
            <h6 style={styles.sectionTitle}>Doctors Available Now</h6>
            <button 
              style={styles.seeAllButton}
              onClick={() => navigate('/all-doctors')}
            >
              See All <ChevronRight size={16} />
            </button>
          </div>
          <div className="doctor-two-row-scroll">
            {doctors.map((doc, idx) => (
              <div className="doctor-card-fixed-width" key={idx}><DoctorCard doctor={doc} /></div>
            ))}
          </div>
        </div>
 {/* Upcoming Appointments */}

  <UpcomingConsultations navigate={navigate} />
            
            <br></br>







{/*Nearby Doctors Section */}

{/* Nearby Doctors 
  <NearbyDoctors
        loadingNearby={loadingNearby}
        nearbyDoctors={nearbyDoctors}
        locationName={locationName}
        navigate={navigate}
      />
*/}
 <div
   style={{
     display: "flex",
     alignItems: "center",
     justifyContent: "space-between",
     marginBottom: 12,
   }}
 >
   <h6 style={styles.sectionTitle}>Top Doctor</h6>
   <button
     style={{
       border: "none",
       background: "transparent",
       color: "#6b7280",
       fontSize: 16,
       fontWeight: 500,
       padding: 0,
     }}
     onClick={() => navigate('/all-doctors')}
   >
     See All
   </button>
 </div>
 <div className="doctor-two-row-scroll">
   {nearbyDoctors.slice(0, 4).map((doc, idx) => (
     <div className="doctor-card-fixed-width" key={idx}><DoctorCard doctor={doc} /></div>
   ))}
 </div>
     
       <br/>
        {/* Recommended Doctors */}
       <div style={styles.sectionHeader}>
                  <h6 style={styles.sectionTitle}>Recommended Doctors</h6>
                  <button 
                    style={styles.seeAllButton}
                     onClick={() => navigate('/all-doctors')}
                  >
                    See All <ChevronRight size={16} />
                  </button>
                </div>

   <div className="doctor-two-row-scroll" style={{ marginBottom: 8 }}>
    {recommend.map((doc, idx) => (
      <div className="doctor-card-fixed-width" key={idx}><DoctorCard doctor={doc} /></div>
    ))}
    </div>

      <NearbyClinicsPlaceholder />

      </div>

    
    </div>
  );
}

function NearbyClinicsPlaceholder() {
  const navigate = useNavigate();
  const fallbackClinics = [
    { name: 'CityCare Clinic', eta: '10-15 min', area: 'Downtown', image: '' },
    { name: 'HealthPoint Medical Center', eta: '15-20 min', area: 'West End', image: '' },
    { name: 'Sunrise Family Clinic', eta: '20-25 min', area: 'Uptown', image: '' },
  ];
  const [clinics, setClinics] = useState(fallbackClinics);

  useEffect(() => {
    let isCancelled = false;

    const fetchNearbyClinics = async () => {
      try {
        const res = await api.get('/api/nearby-clinics?limit=12');
        const payload = Array.isArray(res?.data?.clinics)
          ? res.data.clinics
          : Array.isArray(res?.data)
          ? res.data
          : [];

        if (!isCancelled && payload.length > 0) {
          setClinics(payload);
        }
      } catch (err) {
        console.error('Failed to fetch nearby clinics:', err);
      }
    };

    fetchNearbyClinics();

    return () => {
      isCancelled = true;
    };
  }, []);

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ ...styles.sectionHeader, marginBottom: 8 }}>
        <h6 style={styles.sectionTitle}>Nearby Clinics</h6>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 10,
          overflowX: 'auto',
          paddingBottom: 4,
          scrollSnapType: 'x mandatory',
        }}
      >
        {clinics.map((clinic) => (
          <div
            key={clinic._id || clinic.name}
            onClick={() => navigate(`/clinic/${clinic._id || ''}`, { state: clinic })}
            style={{
              border: '1px solid #e9e8ef',
              borderRadius: 18,
              background: 'var(--surface)',
              width: 320,
              flex: '0 0 320px',
              overflow: 'hidden',
              boxShadow: '0 3px 10px rgba(0,0,0,0.12)',
              scrollSnapAlign: 'start',
              cursor: 'pointer',
            }}
          >
            <div style={{ width: '100%', height: 178, overflow: 'hidden' }}>
              {clinic.image || clinic.logo ? (
                <img
                  src={clinic.image || clinic.logo}
                  alt={clinic.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'rgba(106,58,255,0.12)',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Hospital size={40} color="var(--primary)" />
                </div>
              )}
            </div>

            <div
              style={{
                background: '#efefef',
                padding: '12px 14px 14px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 700, color: '#202125', textAlign: 'center', lineHeight: 1.2 }}>
                {clinic.name}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#2a2b30', marginTop: 4 }}>
                <Star size={12} fill="#FFD700" color="#FFD700" />
                <span style={{ fontSize: 13, fontWeight: 700 }}>4.5</span>
                <span style={{ fontSize: 13 }}>(100)</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function SafetyBanner() {
  const navigate = useNavigate();

  return (
    <div style={styles.safetyBanner}>
      <div style={styles.safetyContent}>
        <div style={styles.safetyIcon}>
          <ShieldCheck style = {{color : "var(--primary)"}} size={22} />
        </div>
        <div>
          <h3 style={styles.safetyTitle}>Licensed and Verified Doctors</h3>
          <p style={styles.safetyText}>
            All doctors are verified and follow local  <br />

            medical guidelines
          </p>
        </div>
      </div>

    </div>
  );
}

function NearbyDoctors({
  loadingNearby,
  nearbyDoctors,
  locationName,
  navigate,
}) {
  const styles = {
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 6,
      marginTop: 16,
    },
    sectionTitle: {
      fontSize: 15,
      fontWeight: 600,
      color: "var(--on-bg)",
      textTransform: "capitalize",
      letterSpacing: "0.4px",
    },
    subtitle: {
      color: "var(--muted)",
      fontSize: 13,
      marginBottom: 12,
    },
    locationBadge: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      background: "linear-gradient(90deg, rgba(103,80,164,0.1), rgba(103,80,164,0.05))",
      padding: "4px 10px",
      borderRadius: 20,
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    },
    locationText: {
      fontSize: 13,
      color: "#333",
      fontWeight: 500,
    },
    cardContainer: {
      display: "flex",
      overflowX: "auto",
      gap: 20,
      paddingBottom: 6,
      scrollBehavior: "smooth",
    },
    card: {
      minWidth: 250,
      backgroundColor: "#fff",
      borderRadius: 16,
      padding: 16,
      position: "relative",
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
      transition: "transform 0.25s ease, box-shadow 0.25s ease",
      cursor: "pointer",
    },
    specialtyTag: {
      position: "absolute",
      right: 12,
      top: 12,
      background: "rgba(103,80,164,0.9)",
      color: "#fff",
      padding: "5px 10px",
      borderRadius: 18,
      fontSize: 12,
      fontWeight: 600,
    },
    avatarWrap: {
      width: 56,
      height: 56,
      borderRadius: 14,
      background: "linear-gradient(135deg, rgba(103,80,164,0.08), rgba(103,80,164,0.02))",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    avatar: {
      width: 48,
      height: 48,
      borderRadius: "50%",
      objectFit: "cover",
    },
    name: {
      fontSize: 15,
      fontWeight: 600,
      color: "#1a1a1a",
      marginBottom: 4,
    },
    statRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      color: "var(--muted)",
      fontSize: 13,
    },
    bottom: {
      marginTop: 10,
    },
    clinic: {
      fontSize: 13,
      color: "var(--muted)",
      marginBottom: 8,
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
    bookBtn: {
      width: "100%",
      background: "var(--primary)",
      color: "#fff",
      fontSize: 13,
      fontWeight: 600,
      borderRadius: 50,
      padding: "8px 0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      border: "none",
      cursor: "pointer",
      transition: "background 0.2s ease",
    },
  };

  return (
    <div>
      {/* Header */}
      <div style={styles.sectionHeader}>
        <div>
          <h6 style={styles.sectionTitle}>Doctors Near You</h6>
        </div>
        <div style={styles.locationBadge}>
          <MapPin size={14} color="#6750A4" />
          <small style={styles.locationText}>{locationName || "Fetching..."}</small>
        </div>
      </div>
      <p style={styles.subtitle}>Top-rated doctors available near your location.</p>

      {/* Doctors List */}
      {loadingNearby ? (
        <p style={{ color: "#777" }}>Loading nearby doctors...</p>
      ) : nearbyDoctors.length === 0 ? (
        <p style={{ color: "#888" }}>No nearby doctors found.</p>
      ) : (
        <div style={styles.cardContainer}>
          {nearbyDoctors.map((doc) => (
            <div
              key={doc._id}
              style={styles.card}
              onClick={() => navigate(`/doctor-details/${doc._id}`)}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-4px)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
            >
              {/* Specialty tag */}
              <span style={styles.specialtyTag}>{doc.specialty || "General"}</span>

              {/* Top */}
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={styles.avatarWrap}>
                  <img
                    src={doc.avatar || "https://randomuser.me/api/portraits/men/7.jpg"}
                    alt={doc.name}
                    style={styles.avatar}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <h6 style={styles.name}>{doc.name}</h6>
                  <div style={styles.statRow}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <Star size={14} color="#facc15" />
                      <small>
                        {doc.rating || 5.0} • {doc.reviewsCount ?? 24}
                      </small>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <MapPin size={13} />
                      <small>{doc.distanceKm ?? 5} km</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom */}
              <div style={styles.bottom}>
                <p style={styles.clinic}>
                  <User size={13} />
                  {doc.clinic || doc.specialty}
                </p>
                <button
                  style={styles.bookBtn}
                  onMouseEnter={(e) => (e.target.style.background = "#4e3d8b")}
                  onMouseLeave={(e) => (e.target.style.background = "var(--accent)")}
                >
                  <span>Book Now</span>
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}




function UpcomingConsultations({ navigate }) {
  const [open, setOpen] = useState(false);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const { profile } = useAuth();
  const user = profile;

  const fetchAppointments = useCallback(async () => {
    if (!user?._id) return;
    setLoadingAppointments(true);
    try {
      const appts = await fetchUpcomingConsultationsWithDoctors(user._id);
      setFilteredAppointments(appts);
    } catch (err) {
      setFilteredAppointments([]);
    } finally {
      setLoadingAppointments(false);
    }
  }, [user]);

  useEffect(() => {
    if (open) {
      fetchAppointments();
    }
  }, [open, fetchAppointments]);

  const styles = {
    card: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "#f8f6fc",
      borderRadius: 12,
      padding: "10px 14px",
      cursor: "pointer",
      marginTop: 12,
    },
    left: {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    text: {
      fontSize: 14,
      color: "#1a1a1a",
      fontWeight: 500,
    },
    subtext: {
      fontSize: 12,
      color: "#666",
    },
  };

  return (
    <div style={{ marginTop: 16 }}>
      {/* Collapsed view */}
      {!open && (
        <div style={styles.card} onClick={() => setOpen(true)}>
          <div style={styles.left}>
            <Info size={18} color="#6750A4" />
            <div>
              <div style={styles.text}>Manage your upcoming consultations</div>
              <div style={styles.subtext}>Book a doctor when you need care</div>
            </div>
          </div>
          <ChevronRight size={18} color="#888" />
        </div>
      )}

      {/* Expanded list */}
      {open && (
        <div>
          <h3
            style={{
              ...styles.sectionTitle,
              marginTop: 16,
              marginBottom: 6,
            }}
          >
            Upcoming Consultations
          </h3>
          <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 12 }}>
            Manage and view your upcoming visits. Keep track of status and times.
          </p>

          {loadingAppointments ? (
            <div>Loading consultations...</div>
          ) : filteredAppointments.length === 0 ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <CalendarX size={80} color="var(--primary)" />
              <p style={{ color: "var(--primary)", marginTop: 8 }}>
                No upcoming Consultation
              </p>
            </div>
          ) : (
            filteredAppointments.slice(0, 3).map((appt) => {
              const status = appt.status || "scheduled";
              const borderColor =
                status === "completed"
                  ? "#10B981"
                  : status === "ongoing"
                  ? "#06b6d4"
                  : "var(--accent)";
              return (
                <div
                  key={appt._id}
                  style={{
                    borderLeft: `4px solid ${borderColor}`,
                    background: "#fff",
                    borderRadius: 12,
                    padding: 12,
                    marginBottom: 12,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <img
                      src={appt.doctor.avatar}
                      alt={appt.doctor.name}
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        objectFit: "cover",
                        marginRight: 12,
                      }}
                    />
                    <div
                      style={{ flex: 1, cursor: "pointer" }}
                      onClick={() => navigate(`/doctor-details/${appt.doctor._id}`)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <h6 style={{ margin: 0, fontWeight: 600 }}>{appt.doctor.name}</h6>
                        <span
                          style={{
                            background: "#f3f4f6",
                            padding: "4px 8px",
                            borderRadius: 12,
                            fontSize: 12,
                            color: "var(--muted)",
                          }}
                        >
                          {appt.doctor.specialty}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 6,
                        }}
                      >
                        <Clock size={14} />
                        <small style={{ color: "#777" }}>
                          {new Date(appt.appointmentTime).toLocaleString()}
                        </small>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          background: "rgba(99,102,241,0.12)",
                          color: "var(--accent)",
                          padding: "4px 8px",
                          borderRadius: 12,
                          fontWeight: 600,
                          fontSize: 12,
                        }}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                      <button
                        onClick={() =>
                          navigate("/appoint", { state: { consultation: appt } })
                        }
                        style={{
                          border: "1px solid #ccc",
                          borderRadius: 50,
                          padding: "6px 10px",
                          fontSize: 13,
                          background: "transparent",
                          cursor: "pointer",
                        }}
                      >
                        Manage
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {/* Back button */}
          <div style={{ textAlign: "right", marginTop: 10 }}>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: "transparent",
                color: "var(--primary)",
                border: "none",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Hide consultations
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
function DoctorCard1({ doctor }) {
  const navigate = useNavigate();
  const isOnline = (doctor.status || '').toLowerCase().includes('online');
  const rating = doctor.rating ?? 4.8;
  const reviews = doctor.reviews ?? 320;
  const fullStars = Math.floor(rating);
  const halfStar = rating - fullStars >= 0.5;

  return (
    <div
      onClick={() => navigate(`/doctor-details/${doctor._id}`)}
      style={{
        display: 'flex',
        flexDirection: 'row',
        width: 'min(100%, 340px)',
        minWidth: 0,
        maxWidth: 340,
        flexShrink: 0,
        backgroundColor: '#ffffff',
        borderRadius: 18,
        overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(2,6,23,0.06)',
        border: '1px solid #eef0f4',
        padding: '12px 12px 10px',
        gap: 12,
        cursor: 'pointer',
      }}
    >
      <div style={{ width: 82, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img
          src={doctor.avatar || 'https://randomuser.me/api/portraits/men/7.jpg'}
          alt={doctor.name || 'Doctor'}
          style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
        />
        <span style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: isOnline ? '#16a34a' : '#6b7280' }}>
          {isOnline ? 'Online' : 'Offline'}
        </span>
      </div>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#111827',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {doctor.name || 'Dr. Unknown'}
        </span>
        <div
          style={{
            fontSize: 11,
            color: '#4b5563',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {doctor.specialty || 'General Practitioner'}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
          <span style={{ color: '#f59e0b', fontSize: 12, letterSpacing: 0.5 }}>
            {'★'.repeat(fullStars)}{halfStar ? '☆' : ''}{'☆'.repeat(Math.max(0, 5 - fullStars - (halfStar ? 1 : 0)))}
          </span>
          <span style={{ fontSize: 11, color: '#374151', fontWeight: 600 }}>{Number(rating).toFixed(1)} ({reviews})</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <MessageSquare size={16} color="#6b7280" />
            <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Chat</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Video size={16} color="#6b7280" />
            <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Video</span>
          </div>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/doctor-details/${doctor._id}`); }}
          style={{
            marginTop: 8,
            width: '100%',
            background: '#f5f3ff',
            color: '#312e81',
            border: '1px solid #ece8ff',
            borderRadius: 14,
            padding: '8px 12px',
            fontSize: 12,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          <MessageSquare size={14} />
          Consult Now
        </button>
      </div>
    </div>
  );
}

function DoctorCardk({ doctor }) {
  const navigate = useNavigate();

  const isOnline = (doctor.status || '').toLowerCase().includes('online');
  const rating = doctor.rating ?? 4.8;
  const reviews = doctor.reviews ?? 320;

  const fullStars = Math.floor(rating);
  const halfStar = rating - fullStars >= 0.5;

  return (
    <div
      onClick={() => navigate(`/doctor-details/${doctor._id}`)}
      style={{
        display: 'flex',
        flexDirection: 'row',
        width: 'min(100%, 340px)',
        minWidth: 0,
        maxWidth: 340,
        flexShrink: 0,
        backgroundColor: '#ffffff',
        borderRadius: 18,
        overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(2,6,23,0.06)',
        border: '1px solid #eef0f4',
        padding: '12px 12px 10px',
        gap: 12,
        cursor: 'pointer',
      }}
    >
      <div style={{ width: 82, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img
          src={doctor.avatar || 'https://randomuser.me/api/portraits/men/7.jpg'}
          alt={doctor.name || 'Doctor'}
          style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
        />
        <span style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: isOnline ? '#16a34a' : '#6b7280' }}>
          {isOnline ? 'Online' : 'Offline'}
        </span>
      </div>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#111827',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {doctor.name || 'Dr. Unknown'}
        </span>
        <div
          style={{
            fontSize: 11,
            color: '#4b5563',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {doctor.specialty || 'General Practitioner'}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
          <span style={{ color: '#f59e0b', fontSize: 12, letterSpacing: 0.5 }}>
            {'★'.repeat(fullStars)}{halfStar ? '☆' : ''}{'☆'.repeat(Math.max(0, 5 - fullStars - (halfStar ? 1 : 0)))}
          </span>
          <span style={{ fontSize: 11, color: '#374151', fontWeight: 600 }}>{Number(rating).toFixed(1)} ({reviews})</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <MessageSquare size={16} color="#6b7280" />
            <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Chat</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Video size={16} color="#6b7280" />
            <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Video</span>
          </div>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/doctor-details/${doctor._id}`); }}
          style={{
            marginTop: 8,
            width: '100%',
            background: '#f5f3ff',
            color: '#312e81',
            border: '1px solid #ece8ff',
            borderRadius: 14,
            padding: '8px 12px',
            fontSize: 12,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          <MessageSquare size={14} />
          Consult Now
        </button>
      </div>
    </div>
  );
}



function HealthCategories() {

    const categories = [
    { name: "Gastroenterologist", icon: digestion },
    { name: "Cardiologist", icon: physician },
    { name: "Neurologist", icon: brain },
    { name: "General Surgeon", icon: surgeon },
  ];

  const navigate = useNavigate();


  const styles = {
    container: {
      
    },

    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 12,
    },

    title: {
      fontSize: 14,
      fontWeight: 700,
      color: "#1f1f1f",
    },

    viewAll: {
      fontSize: 12,
      color: "#6a6a6a",
      cursor: "pointer",
    },

 list: {
  display: "flex",
  flexDirection: "row",
  overflowX: "auto",
  gap: 12,
},

   card: {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  backgroundColor: "var(--surface)",
  borderRadius: 30,
  height : 60,
    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
  fontSize: 12,
  fontWeight: 600,
  color: "#333",
  border: "1px solid #ededed",
},

    icon: {
      fontSize: 14,
    },
  };

  return (
    <div style={styles.container}>
      

      {/* CATEGORY LIST */}
      <div style={styles.list}>
        {categories.map((item, index) => (
          <div key={index} style={styles.card}>
             <img src={item.icon}  style={{ width: 35, height: 35, padding : "4px", borderRadius : "50%", border: "solid var(--bg)" }} />
            <span style={styles.icon}>{item.name}</span>
           
          </div>
        ))}
      </div>
    </div>
  );
}


function AIDoctorBanner() {
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const navigate = useNavigate();
  const bannerImages = [dc1, dc2, dc3];

  useEffect(() => {
    if (bannerImages.length <= 1) return undefined;

    const interval = window.setInterval(() => {
      setActiveBannerIndex((prev) => (prev + 1) % bannerImages.length);
    }, 3500);

    return () => window.clearInterval(interval);
  }, []);

  const styles = {
    banner: {
      width: "100%",
      height: 170,
      borderRadius: 20,
      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
      overflow: "hidden",
      border: "1px solid #fffdfdff",
      position: "relative",
    },
    image: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    },
  };

  return (
    <div style={styles.banner}>
      <img
        src={bannerImages[activeBannerIndex]}
        alt={`Doctor banner ${activeBannerIndex + 1}`}
        style={styles.image}
      />
      <div
        style={{
          position: "absolute",
          right: 16,
          bottom: 12,
          display: "flex",
          gap: 6,
          zIndex: 3,
        }}
      >
        {bannerImages.map((_, index) => (
          <button
            key={`doctor-ai-banner-${index}`}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setActiveBannerIndex(index);
            }}
            style={{
              width: activeBannerIndex === index ? 20 : 7,
              height: 7,
              borderRadius: 999,
              border: "none",
              background: activeBannerIndex === index ? "#ffffff" : "rgba(255,255,255,0.5)",
              transition: "all 0.2s ease",
              cursor: "pointer",
              padding: 0,
            }}
          ></button>
        ))}
      </div>
    </div>
  );
}




const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--bg)',
    paddingBottom: '800px',
  },
    seeAllButton: {
    background: 'none',
    border: 'none',
    color: 'var(--primary)',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
  },
   header: {
    background: "var(--bg)",
    color: "var(--on-bg)",
    position: "relative",
    width: "100%",
    overflow: "visible",
    borderBottomLeftRadius: 25,
    borderBottomRightRadius: 25,
    marginBottom: 20,
    padding: "18px 0 8px",
    fontFamily: "'Poppins', sans-serif",
  },
  headerTop: {
    display: "flex",
    marginTop: 0,
    justifyContent: "space-between",
    alignItems: "center",
  },
  backArrow: {
    cursor: 'pointer',
    flexShrink: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "var(--primary)",
    marginTop: 4,
  },
  headerTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: 22,
    marginLeft: 12,
    color: "var(--on-bg)",
    letterSpacing: "0.3px",
    textAlign: "center",
    lineHeight: 1.15,
  },
   section: {

  marginBottom : 24,
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "var(--on-bg)",
    textTransform: "capitalize",
    letterSpacing: "0.4px",
  },
 categoriesGrid: {
  display: "flex",
  overflowX: "auto",      
  gap: 12,
  padding: "0 8px",
  scrollSnapType: "x mandatory",
},
 headerSubtitle: {
    fontSize: 12,
   marginTop: 4,
   marginLeft: 34,
    textAlign: "left",
  },
  trustBadge: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
    marginLeft: 34,
    marginTop: -7
  },
  trustStrip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 6,
    marginBottom: 10,
    marginTop: 8,
    background: "#ffffff",
    border: "1px solid #eceef1",
    borderRadius: 999,
    padding: "6px 8px",
    overflowX: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  trustTrack: {
    display: "flex",
    alignItems: "center",
    width: "max-content",
    gap: 6,
  },
  trustItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 142,
  },
  trustItemIconWrap: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    border: "1px solid #eceef1",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  trustItemCopy: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.15,
  },
  trustItemTitle: {
    fontSize: 11,
    color: "#3D3A52",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  trustItemSubtitle: {
    fontSize: 9,
    color: "#A0A0B2",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  trustStripDivider: {
    width: 1,
    height: 20,
    background: "#d6d2e7",
    flexShrink: 0,
  },
  trustIcon: {
    fontSize: 14,
    color: "green"
  },
  trustText: {
    fontSize: "0.85em",
    left: "-10px",
    lineHeight: "1",
  },
categoryCard: {
  position: "relative",
  flex: "0 0 80px",        // smaller width per card
  scrollSnapAlign: "start",
  backgroundColor: "var(--surface)",
  borderRadius: 12,        // slightly smaller radius for smaller card
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  aspectRatio: "1 / 1",    // slightly more square
  cursor: "pointer",
  boxShadow: "0 2px 4px rgba(0,0,0,0.08)", // lighter shadow
  transition: "transform 0.2s, box-shadow 0.2s",
},


  categoryImageWrapper: {
    width: "100%",
    height: "50%", // reduce image height slightly to allow text space
  },
  categoryImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  categoryLabel: {
    fontSize: 9.5, // smaller text
    fontWeight: "bold",
    textAlign: "center",
    color: "var(--on-bg)",
    marginTop: 6,
    padding: "0 6px", // keeps spacing consistent
    lineHeight: 1.4,
    wordWrap: "break-word",
    overflow: "visible",
    whiteSpace: "normal",
  },
  seeAllButton: {
    background: 'none',
    border: 'none',
    color: 'var(--primary)',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
  },
  categoriesContainer: {
    display: 'flex',
    overflowX: 'auto',
    gap: '12px',
    paddingBottom: '8px',
    scrollbarWidth: 'none',
  },
  categoryPill: {
    padding: '12px 20px',
    background: 'var(--surface)',
    borderRadius: '24px',
    color: 'var(--on-bg)',
    fontSize: '14px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    
    alignItems: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid var(--border)',
  },

    card: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "#fff",
      borderRadius: 16,
      padding: "12px 16px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      marginBottom: 12,
      border: "1px solid #f0f0f0",
    },
    safetyBanner: {
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
    color: "#374151",
    marginTop: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  safetyContent: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  safetyIcon: {
    background: "inherit",
    color: "#065F46",
    borderRadius: "50%",
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  safetyTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 600,
  },
  safetyText: {
    margin: 0,
    fontSize: 13,
    color: "#6B7280",
    lineHeight: 1.4,
  },
  learnMoreButton: {
    background: "#E0F2FE",
    color: "#0369A1",
    border: "none",
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.2s",
  },
    left: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    avatar: {
      width: 46,
      height: 46,
      borderRadius: "50%",
      objectFit: "cover",
    },
    name: {
      fontSize: 15,
      fontWeight: 600,
      color: "#1a1a1a",
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
    verified: {
      display: "flex",
      alignItems: "center",
      background: "rgba(103,80,164,0.1)",
      color: "#6750A4",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 8,
      padding: "2px 6px",
    },
    specialty: {
      fontSize: 13,
      color: "#666",
      marginTop: 2,
    },
    status: {
      fontSize: 12,
      color: "green",
      fontWeight: 500,
      marginTop: 4,
    },
    right: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 6,
    },
    button: {
      background: "#6750A4",
      color: "#fff",
      border: "none",
      borderRadius: 12,
      padding: "6px 14px",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
    },
    subtext: {
      fontSize: 12,
      color: "#7a7a7a",
    },
}
